import NavigationIcon from "@mui/icons-material/Navigation";
import SquareRoundedIcon from "@mui/icons-material/SquareRounded";
import Tooltip from "@mui/material/Tooltip";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { DeviceActivity } from "@/utilities/deviceActivity";

/**
 * Whether a vehicle is moving, standing or silent, to sit in front of its name.
 *
 * An arrow for moving and a square otherwise, greyed out where the vehicle is not reporting — so
 * the shape says what it is doing and the colour says whether that is current. The label is both
 * the tooltip and the icon's accessible name, because the shape alone does not name the state.
 */
export function DeviceActivityIcon({ activity }: { activity: DeviceActivity }) {
  const { t } = useTranslation();

  const label = deviceActivityLabel(activity, t);
  const isReporting = activity === "driving" || activity === "stopped";
  const Icon = activity === "driving" ? NavigationIcon : SquareRoundedIcon;

  return (
    <Tooltip title={label}>
      <Icon
        color={isReporting ? "primary" : "disabled"}
        fontSize="small"
        titleAccess={label}
        sx={{ flexShrink: 0 }}
      />
    </Tooltip>
  );
}

/** The wording for an activity, which is both the tooltip and the accessible name. */
function deviceActivityLabel(activity: DeviceActivity, t: TFunction): string {
  switch (activity) {
    case "driving":
      return t("deviceStatus.activity.driving", "Fährt");
    case "stopped":
      return t("deviceStatus.activity.stopped", "Steht");
    case "offline":
      return t("deviceStatus.activity.offline", "Offline");
    default:
      return t("deviceStatus.activity.unknown", "Status noch nicht bekannt");
  }
}
